import axiosInstance from "@/axios";
import { describeError } from "@/utils/safeErrorLog";

/**
 * useResolveBatch -- cart-scoped resolver for the variant model.
 *
 * Wraps ``POST /api/placements/resolve_batch`` (P6 PR-A; renamed
 * from ``/api/decorations/resolve_batch`` and re-keyed from
 * ``decoration_id`` to ``placement_id`` per pair when the SAFRS
 * source for ``decoration_records`` swung from
 * ``CampaignProduct.decorations`` to ``CampaignProduct.placements``).
 * Used by the shopper flow to ask "what variant routes each
 * (placement_id, color_sku) pair?" so the cart preview, order
 * display, and storefront cart can append decoration labels for the
 * lines that actually carry a routed variant.
 *
 * Public surface (locked)
 * =======================
 *
 *   const { resolveBatch } = useResolveBatch()
 *
 *   resolveBatch(pairs): Promise<Map>
 *     pairs: Array<{ placement_id: number, color_sku: string }>
 *     returns: Map keyed by `${placement_id}|${color_sku}` ->
 *              { variant_id, artwork_file_id, kind, hint }
 *
 * Why a Map keyed by ``placement_id|color_sku`` rather than the
 * server's positional array: the shopper-flow consumers (5 files)
 * iterate ``decoration_records`` per cart line, not the request
 * array. A keyed lookup avoids reconstructing the input order
 * inside each consumer.
 *
 * Concurrency
 * ===========
 *
 * Callers can fire ``resolveBatch`` whenever the cart changes. The
 * composable returns a fresh promise each call -- it does NOT
 * de-dupe in-flight requests against equal inputs. The shopper flow
 * is throttled by the cart-render reactive cycle (Vue's micro-task
 * batching), so duplicate calls are rare.
 *
 * Errors
 * ======
 *
 * Network-level failures (4xx, 5xx, timeout) are CAUGHT and
 * converted to an empty Map. Rationale: a transient resolver
 * failure should fall back to "no label" rather than blowing up
 * the entire cart render. The underlying error is logged via
 * ``console.warn`` so it remains observable in the browser console
 * / smoke runs without surfacing to the shopper as a broken page.
 *
 * Empty / no-op inputs
 * ====================
 *
 * - Empty array of pairs returns an immediately-resolved empty Map
 *   without a round-trip. The cart with no decoration-bearing lines
 *   is a common case (shopper just added a blank product).
 * - Duplicate pairs are silently de-duped on the request wire so the
 *   server doesn't resolve the same (placement_id, color_sku) twice.
 *   The response Map carries the deduped key once; consumers look up
 *   the same key regardless of how many cart lines share it.
 */
export function useResolveBatch() {
    const pairKey = (placement_id, color_sku) =>
        `${placement_id}|${color_sku}`;

    const resolveBatch = async (pairs) => {
        const out = new Map();
        if (!Array.isArray(pairs) || pairs.length === 0) {
            return out;
        }

        // De-dupe at the wire boundary. The shopper flow can ask for
        // the same (placement_id, color_sku) for every size variant
        // of a CampaignProduct -- one round-trip resolves once and
        // the Map serves N lookups.
        const seen = new Set();
        const wirePairs = [];
        for (const p of pairs) {
            if (
                !p ||
                p.placement_id == null ||
                p.color_sku == null ||
                p.color_sku === ""
            ) {
                continue;
            }
            const key = pairKey(p.placement_id, p.color_sku);
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            wirePairs.push({
                placement_id: p.placement_id,
                color_sku: p.color_sku,
            });
        }
        if (wirePairs.length === 0) {
            return out;
        }

        try {
            const resp = await axiosInstance.post(
                "/api/placements/resolve_batch",
                { resolutions: wirePairs },
                {
                    // Resolver is NOT a SAFRS endpoint -- override the
                    // axiosInstance default JSON:API content-type so the
                    // body parses as plain application/json.
                    headers: { "Content-Type": "application/json" },
                }
            );
            const rows = (resp && resp.data && resp.data.resolutions) || [];
            for (const row of rows) {
                if (
                    row &&
                    row.placement_id != null &&
                    row.color_sku != null
                ) {
                    out.set(pairKey(row.placement_id, row.color_sku), {
                        variant_id: row.variant_id ?? null,
                        artwork_file_id: row.artwork_file_id ?? null,
                        kind: row.kind ?? null,
                        hint: row.hint ?? "no_variant",
                    });
                }
            }
        } catch (err) {
            // Fall back to "no label" on resolver failure -- a
            // transient resolver problem must not blow up the cart
            // render. The warn surface keeps the failure observable
            // in browser-console / smoke runs.
            // eslint-disable-next-line no-console
            console.warn(
                "[useResolveBatch] resolve_batch failed; suppressing " +
                    "labels for this render",
                describeError(err)
            );
        }
        return out;
    };

    return { resolveBatch, pairKey };
}
